/**
 * Support — WhatsApp Support Center for admins.
 *
 * iter-238 (Feb 2026) — This file is now a thin container. All UI panels
 * live in `../components/support/*`:
 *   • atoms (constants + StatusBadge + ChannelIcon + formatters)
 *   • ConversationList (left)
 *   • ChatArea (middle) — messages + header + input
 *   • ContactDetailsPanel (right)
 *   • SupportDialogs (New Chat / Transfer / Confirm Archive+Delete)
 *
 * The container owns every piece of state and the network calls; children
 * receive their slice via props so testids and behaviour stay identical.
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { ConversationList } from '../components/support/ConversationList';
import { ChatArea } from '../components/support/ChatArea';
import { ContactDetailsPanel } from '../components/support/ContactDetailsPanel';
import { NewChatDialog, TransferDialog, ConfirmDialog } from '../components/support/SupportDialogs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Support() {
  const { user: currentUser, getAuthHeaders } = useAuth();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [admins, setAdmins] = useState([]);

  // Dialogs
  const [showNewChat, setShowNewChat] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState('');
  const [newChatName, setNewChatName] = useState('');
  const [transferTo, setTransferTo] = useState('');
  const [showDetailsPanel, setShowDetailsPanel] = useState(true);
  const [contactDetails, setContactDetails] = useState(null);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState({ name: '', email: '', notes: '', tags: '' });
  // iter-237 — confirm dialogs for Archive + Delete.
  const [confirmDialog, setConfirmDialog] = useState(null); // { type: 'archive'|'delete', phone }

  const messagesEndRef = useRef(null);
  const headers = getAuthHeaders();

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/whatsapp/conversations`, { headers });
      setConversations(res.data.data || []);
    } catch (err) {
      console.log('Conversations fetch:', err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/whatsapp/admins`, { headers });
      setAdmins(res.data.data || []);
    } catch (err) {
      console.log('Admins fetch:', err.message);
    }
  };

  const fetchMessages = async (phone) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/whatsapp/messages/${phone}`, { headers });
      setMessages(res.data.data || []);
    } catch (err) {
      console.log('Messages fetch:', err.message);
    }
  };

  const fetchContactDetails = async (phone) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/whatsapp/contact/${phone}/details`, { headers });
      setContactDetails(res.data);
      const c = res.data.contact || {};
      setDetailsDraft({
        name: c.name || '',
        email: c.email || '',
        notes: c.notes || '',
        tags: (c.tags || []).join(', '),
      });
    } catch (err) {
      console.log('Contact details fetch:', err.message);
    }
  };

  const saveContactDetails = async () => {
    if (!selectedContact) return;
    setDetailsSaving(true);
    try {
      const payload = {
        name: detailsDraft.name.trim(),
        email: detailsDraft.email.trim(),
        notes: detailsDraft.notes,
        tags: detailsDraft.tags.split(',').map((tg) => tg.trim()).filter(Boolean),
      };
      const res = await axios.patch(
        `${BACKEND_URL}/api/whatsapp/contact/${selectedContact.phone}`,
        payload,
        { headers }
      );
      setContactDetails((prev) => (prev ? { ...prev, contact: res.data.contact } : prev));
      setSelectedContact((c) => (c ? { ...c, ...res.data.contact } : c));
      fetchConversations();
      toast.success(language === 'ar' ? 'تم حفظ البيانات' : 'Details saved');
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل حفظ البيانات' : 'Save failed');
    } finally {
      setDetailsSaving(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    fetchAdmins();
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedContact) {
      fetchMessages(selectedContact.phone);
      fetchContactDetails(selectedContact.phone);
      const msgInterval = setInterval(() => fetchMessages(selectedContact.phone), 3000);
      return () => clearInterval(msgInterval);
    } else {
      setContactDetails(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    let filtered = conversations;
    if (searchQuery) {
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.phone?.includes(searchQuery)
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }
    setFilteredConversations(filtered);
  }, [conversations, searchQuery, statusFilter]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedContact) return;

    const messageText = newMessage;
    setSending(true);
    // Optimistically clear the input so user sees the send happening.
    setNewMessage('');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/whatsapp/send`,
        null,
        { headers, params: { to: selectedContact.phone, message: messageText } }
      );
      fetchMessages(selectedContact.phone);
      // Only show a notice when we had to auto-send an opening template
      // (24h window closed). Otherwise stay silent — ticks reflect delivery.
      if (res.data?.used_template) {
        toast.message(
          language === 'ar'
            ? 'تم إرسال قالب افتتاحي لفتح نافذة المحادثة'
            : 'Sent an opening template to open the 24h window',
          {
            description: language === 'ar'
              ? 'رسالتك النصية تنتظر رد العميل حتى تُقبل من WhatsApp.'
              : 'Your freeform text will be accepted after the customer replies.'
          }
        );
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Restore input on failure so user can retry/edit
      setNewMessage(messageText);
      toast.error(
        language === 'ar'
          ? (detail ? `فشل الإرسال: ${detail}` : 'فشل إرسال الرسالة')
          : (detail ? `Send failed: ${detail}` : 'Message send failed')
      );
    } finally {
      setSending(false);
    }
  };

  const startNewConversation = async () => {
    if (!newChatPhone.trim()) {
      toast.error(t('أدخل رقم الهاتف', 'Enter phone number'));
      return;
    }
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/whatsapp/start-conversation`,
        null,
        { headers, params: { phone: newChatPhone, name: newChatName || (isAr ? 'عميل جديد' : 'New customer') } }
      );
      setShowNewChat(false);
      setNewChatPhone('');
      setNewChatName('');
      fetchConversations();
      // Select the new/existing conversation
      setSelectedContact(res.data.contact);
      toast.success(res.data.exists
        ? t('تم فتح المحادثة', 'Conversation opened')
        : t('تم إنشاء محادثة جديدة', 'New conversation created'));
    } catch (err) {
      toast.error(t('فشل إنشاء المحادثة', 'Failed to create conversation'));
    }
  };

  const transferConversation = async () => {
    if (!transferTo || !selectedContact) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/whatsapp/transfer`,
        null,
        { headers, params: { phone: selectedContact.phone, to_admin_email: transferTo } }
      );
      setShowTransfer(false);
      setTransferTo('');
      fetchConversations();
      fetchMessages(selectedContact.phone);
      toast.success(t('تم تحويل المحادثة بنجاح', 'Conversation transferred'));
    } catch (err) {
      toast.error(t('فشل تحويل المحادثة', 'Transfer failed'));
    }
  };

  const updateStatus = async (phone, status) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/whatsapp/conversation/${phone}/status`,
        null,
        { headers, params: { status } }
      );
      fetchConversations();
      toast.success(t('تم تحديث الحالة', 'Status updated'));
    } catch (err) {
      toast.error(t('فشل تحديث الحالة', 'Status update failed'));
    }
  };

  const assignToMe = async (phone) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/whatsapp/assign/${phone}`,
        null,
        { headers }
      );
      fetchConversations();
      toast.success(t('تم تعيين المحادثة لك', 'Conversation assigned to you'));
    } catch (err) {
      toast.error(t('فشل تعيين المحادثة', 'Assignment failed'));
    }
  };

  const sendToAudit = async (phone) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/whatsapp/conversation/${phone}/send-to-audit`,
        null,
        { headers }
      );
      // Remove the archived conversation from the current view
      setSelectedContact(null);
      setMessages([]);
      fetchConversations();
      toast.success(t('تم إرسال المحادثة للتدقيق', 'Sent to audit'));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        detail
          ? t(`فشل الإرسال للتدقيق: ${detail}`, `Send to audit failed: ${detail}`)
          : t('فشل إرسال المحادثة للتدقيق', 'Send to audit failed')
      );
    }
  };

  // iter-237 (Feb 2026) — Soft-archive a conversation (hides it from the
  // active list but keeps every message on disk for later reference).
  const archiveConversation = async (phone) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/whatsapp/conversation/${phone}/archive`,
        null,
        { headers },
      );
      setSelectedContact(null);
      setMessages([]);
      setConfirmDialog(null);
      fetchConversations();
      toast.success(t('تم أرشفة المحادثة', 'Conversation archived'));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || t('فشل أرشفة المحادثة', 'Archive failed'));
    }
  };

  // iter-237 (Feb 2026) — Hard-delete the conversation AND all its messages
  // permanently. No undo — the confirm dialog owns the safety net.
  const deleteConversation = async (phone) => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/whatsapp/conversation/${phone}`,
        { headers },
      );
      setSelectedContact(null);
      setMessages([]);
      setConfirmDialog(null);
      fetchConversations();
      toast.success(t('تم حذف المحادثة نهائياً', 'Conversation permanently deleted'));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || t('فشل حذف المحادثة', 'Delete failed'));
    }
  };

  return (
    <DashboardLayout
      title="مركز الدعم"
      titleEn="Support Center"
      subtitle="إدارة محادثات WhatsApp"
      subtitleEn="Manage WhatsApp Chats"
      backPath="/dashboard?platform=support"
      breadcrumb={[
        { labelAr: 'الدعم والمساعدة', labelEn: 'Support & Help', path: '/dashboard?platform=support' },
        { labelAr: 'مركز الدعم', labelEn: 'Support Center' },
      ]}
    >
      {/* Chat Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden h-[calc(100vh-220px)] flex">

          <ConversationList
            isAr={isAr}
            t={t}
            language={language}
            loading={loading}
            filteredConversations={filteredConversations}
            selectedContact={selectedContact}
            setSelectedContact={setSelectedContact}
            setShowNewChat={setShowNewChat}
            fetchConversations={fetchConversations}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
          />

          <ChatArea
            isAr={isAr}
            t={t}
            selectedContact={selectedContact}
            messages={messages}
            messagesEndRef={messagesEndRef}
            newMessage={newMessage}
            setNewMessage={setNewMessage}
            sending={sending}
            sendMessage={sendMessage}
            assignToMe={assignToMe}
            showDetailsPanel={showDetailsPanel}
            setShowDetailsPanel={setShowDetailsPanel}
            setShowTransfer={setShowTransfer}
            updateStatus={updateStatus}
            sendToAudit={sendToAudit}
            setConfirmDialog={setConfirmDialog}
          />

          {selectedContact && showDetailsPanel && (
            <ContactDetailsPanel
              language={language}
              isAr={isAr}
              t={t}
              selectedContact={selectedContact}
              contactDetails={contactDetails}
              detailsDraft={detailsDraft}
              setDetailsDraft={setDetailsDraft}
              detailsSaving={detailsSaving}
              saveContactDetails={saveContactDetails}
              setShowDetailsPanel={setShowDetailsPanel}
            />
          )}
        </div>
      </div>

      <NewChatDialog
        open={showNewChat}
        onOpenChange={setShowNewChat}
        language={language}
        t={t}
        newChatPhone={newChatPhone}
        setNewChatPhone={setNewChatPhone}
        newChatName={newChatName}
        setNewChatName={setNewChatName}
        startNewConversation={startNewConversation}
      />

      <TransferDialog
        open={showTransfer}
        onOpenChange={setShowTransfer}
        t={t}
        admins={admins}
        currentUser={currentUser}
        transferTo={transferTo}
        setTransferTo={setTransferTo}
        transferConversation={transferConversation}
      />

      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
        t={t}
        archiveConversation={archiveConversation}
        deleteConversation={deleteConversation}
      />
    </DashboardLayout>
  );
}
